import React, {
  createContext,
  useContext,
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View, Horizontal, Vertical, ViewProps } from 'app-studio';
import { AccordionContextType } from './Accordion.type';
import {
  AccordionTriggerProps,
  AccordionContentProps,
  AccordionItemProps,
} from './Accordion.props';
import { AccordionShapes, AccordionVariants } from './Accordion.style';
import { deepMerge, useDesignSystemComponentProps } from 'src/design-system';
// Initializes the Accordion context with default values, providing a centralized store for managing accordion state and functionality across all related components.
const AccordionContext = createContext<AccordionContextType>({
  expandedItems: [],
  toggleItem: () => {},
  isItemExpanded: () => false,
  type: 'single',
  collapsible: false,
  baseId: '',
});
// The AccordionProvider component makes the Accordion context available to its children, encapsulating the state and logic for accordion behavior.
export const AccordionProvider: React.FC<{
  children: React.ReactNode;
  value: AccordionContextType;
}> = ({ children, value }) => {
  return (
    <AccordionContext.Provider value={value}>
      {children}
    </AccordionContext.Provider>
  );
};
export const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(
      'useAccordionContext must be used within an AccordionProvider'
    );
  }
  return context;
};
export const AccordionItem: React.FC<AccordionItemProps> = ({
  value,
  children,
  isDisabled = false,
  views,
  ...props
}) => {
  const { isItemExpanded, baseId } = useAccordionContext();
  const designSystemAccordion = useDesignSystemComponentProps('accordion');
  const mergedViews = deepMerge(
    {
      item: designSystemAccordion.views?.container,
    },
    views
  );
  const isExpanded = isItemExpanded(value);
  const triggerId = `${baseId}-trigger-${value}`;
  const contentId = `${baseId}-content-${value}`;
  return (
    <View
      borderWidth={1}
      borderStyle="solid"
      borderColor="color-gray-200"
      overflow="hidden"
      opacity={isDisabled ? 0.5 : 1}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      data-state={isExpanded ? 'open' : 'closed'}
      data-disabled={isDisabled ? '' : undefined}
      {...mergedViews?.item}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<Record<string, unknown>>(child)) {
          return React.cloneElement(child, {
            ...child.props,
            value,
            isExpanded,
            isDisabled,
            triggerId,
            contentId,
          });
        }
        return child;
      })}
    </View>
  );
};
export const AccordionTrigger: React.FC<
  AccordionTriggerProps & {
    value?: string;
    isExpanded?: boolean;
    isDisabled?: boolean;
    triggerId?: string;
    contentId?: string;
  }
> = ({
  children,
  value,
  isExpanded,
  isDisabled,
  triggerId,
  contentId,
  views,
  asChild = false,
  ...props
}) => {
  const { toggleItem } = useAccordionContext();
  const designSystemAccordion = useDesignSystemComponentProps('accordion');
  const mergedViews = deepMerge(
    {
      container: designSystemAccordion.views?.trigger,
    },
    views
  );
  const handleClick = () => {
    if (value && !isDisabled) {
      toggleItem(value);
    }
  };
  const triggerProps = {
    id: triggerId,
    'aria-expanded': isExpanded,
    'aria-controls': contentId,
    'aria-disabled': isDisabled,
    'data-state': isExpanded ? 'open' : 'closed',
    'data-disabled': isDisabled ? '' : undefined,
    onClick: handleClick,
    padding: 16,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    style: { backgroundColor: 'transparent' },
    ...mergedViews?.container,
    ...props,
  };
  if (asChild && isValidElement<Record<string, unknown>>(children)) {
    const child = Children.only(children);
    return cloneElement(child, { ...triggerProps, ...child.props });
  }
  return (
    <Horizontal {...triggerProps}>
      {children}
      <View
        width={24}
        height={24}
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="transform 0.2s ease"
        transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
        {...mergedViews?.icon}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.59 8.59L12 13.17L7.41 8.59L6 10L12 16L18 10L16.59 8.59Z"
            fill="currentColor"
          />
        </svg>
      </View>
    </Horizontal>
  );
};
export const AccordionContent: React.FC<
  AccordionContentProps & {
    isExpanded?: boolean;
    isDisabled?: boolean;
    triggerId?: string;
    contentId?: string;
  }
> = ({
  children,
  isExpanded,
  isDisabled,
  triggerId,
  contentId,
  views,
  ...props
}) => {
  const designSystemAccordion = useDesignSystemComponentProps('accordion');
  const mergedViews = deepMerge(
    {
      container: designSystemAccordion.views?.content,
    },
    views
  );
  const transitionMs = 260;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [shouldRender, setShouldRender] = useState(Boolean(isExpanded));
  const [maxHeight, setMaxHeight] = useState(isExpanded ? 'none' : '0px');
  const [opacity, setOpacity] = useState(isExpanded ? 1 : 0);
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (isExpanded) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        const nextHeight = contentRef.current?.scrollHeight ?? 0;
        setMaxHeight(`${nextHeight}px`);
        setOpacity(1);
      });
      return;
    }
    const currentHeight = contentRef.current?.scrollHeight ?? 0;
    setMaxHeight(`${currentHeight}px`);
    setOpacity(1);
    requestAnimationFrame(() => {
      setMaxHeight('0px');
      setOpacity(0);
    });
    closeTimeoutRef.current = setTimeout(() => {
      setShouldRender(false);
    }, transitionMs);
  }, [isExpanded, children]);
  useEffect(() => {
    if (!isExpanded || !shouldRender) return;
    const nextHeight = contentRef.current?.scrollHeight ?? 0;
    setMaxHeight(`${nextHeight}px`);
  }, [children, isExpanded, shouldRender]);
  if (!shouldRender) {
    return null;
  }
  return (
    <View
      id={contentId}
      role="region"
      aria-labelledby={triggerId}
      overflow="hidden"
      maxHeight={maxHeight}
      opacity={opacity}
      transition={`max-height ${transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${transitionMs}ms ease`}
      data-state={isExpanded ? 'open' : 'closed'}
      data-disabled={isDisabled ? '' : undefined}
      style={{ backgroundColor: 'transparent' }}
      {...mergedViews?.container}
      {...props}
    >
      <View ref={contentRef} padding={16}>
        {children}
      </View>
    </View>
  );
};
export const AccordionView: React.FC<
  {
    children: React.ReactNode;
    shape?: 'square' | 'rounded';
    variant?: 'default' | 'outline' | 'filled';
    views?: any;
    baseId: string;
    type: 'single' | 'multiple';
    collapsible: boolean;
  } & ViewProps
> = ({
  children,
  shape = 'rounded',
  variant = 'default',
  views,
  baseId,
  type,
  collapsible,
  themeMode: elementMode,
  ...props
}) => {
  return (
    <Vertical
      width="100%"
      data-orientation="vertical"
      {...AccordionShapes[shape]}
      {...AccordionVariants[variant]}
      {...views?.container}
      {...props}
    >
      {children}
    </Vertical>
  );
};
